import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, BarChart, Bar, Cell, LabelList,
} from 'recharts';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import CentreDashboard from './CentreDashboard';
import VendorDashboard from './VendorDashboard';
import {
  CHART, fmtINR, fmtNum, useLivePoll, LivePulse, KpiTile, ChartCard, CosmicTooltip, StatusChip, LiveClock, DeltaBadge, monthKeyFromLabel,
} from '../components/analytics/kit';
import DrillDown, { DrillHint } from '../components/analytics/DrillDown';

// ════════════════════════════════════════════════════════════════════════════
//  ADMIN COMMAND CENTER — real-time analytics over the live API.
//  Every number is a real MongoDB aggregation (/admin/summary, /admin/centres,
//  /admin/orders), re-polled every 15s with animated deltas.
// ════════════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const isCentre = user?.role === 'CENTRE';
  const isVendor = user?.role === 'VENDOR';

  const [summary, setSummary] = useState(null);
  const [centres, setCentres] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // which chart segment is open in the drill-down modal: {type, key} or null
  const [drill, setDrill] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!isAdmin) return;
    const [s, t, o] = await Promise.all([
      api.get('/admin/summary'),
      api.get('/admin/centres'),
      api.get('/admin/orders'),
    ]);
    setSummary(s.data?.data || null);
    setCentres(t.data?.data || []);
    setOrders((o.data?.data || []).slice(0, 7));
    setLoading(false);
  }, [isAdmin]);

  const { ago, refresh } = useLivePoll(fetchAll, 10000);

  // REALTIME PUSH: refresh the instant the backend reports a new order/donation.
  useEffect(() => {
    const socket = window.socket;
    if (!socket || !isAdmin) return;
    const onEvent = () => refresh();
    socket.on('analytics_event', onEvent);
    socket.on('order_status_updated', onEvent);
    return () => { socket.off('analytics_event', onEvent); socket.off('order_status_updated', onEvent); };
  }, [refresh, isAdmin]);

  // Role-specific dashboards keep their own command centers
  if (isCentre) return <CentreDashboard />;
  if (isVendor) return <VendorDashboard />;

  if (loading || !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gold/20 border-t-gold"></div>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-muted">Awakening the Command Center…</p>
        </div>
      </div>
    );
  }

  const stats = summary.stats || {};
  const chartData = (summary.chartData || []).map(m => ({
    month: m.month,
    Donations: Math.round(m.donation || 0),
    Offerings: Math.round(m.activity || 0),
    Revenue: Math.round(m.revenue || 0),
  }));
  const donationSum = chartData.reduce((s, m) => s + m.Donations, 0);
  const offeringSum = chartData.reduce((s, m) => s + m.Offerings, 0);
  const orderRevenue = Math.max(0, Math.round((stats.totalRevenue || 0) - donationSum - offeringSum));
  const mixData = [
    { name: 'Store Orders', value: orderRevenue, streamKey: 'orders' },
    { name: 'Donations', value: donationSum, streamKey: 'donations' },
    { name: 'Offerings', value: offeringSum, streamKey: 'offerings' },
  ];
  // recharts chart-level click → open the segment the cursor was on
  const chartDrill = (pick) => (e) => {
    const p = e?.activePayload?.[0]?.payload;
    const next = p && pick(p);
    if (next) setDrill(next);
  };
  const revSpark = chartData.map(m => ({ v: m.Revenue }));
  const liveCentres = centres.filter(t => t.isLive);

  return (
    <div className="min-h-screen p-6 md:p-8">
      {/* ── Header ── */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold-deep mb-1.5">Ananda Marga · Seva Control Room</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold italic text-ivory">
            Command <span className="text-gradient-gold not-italic" style={{ background: 'linear-gradient(100deg,#F6DC96,#E0B355 45%,#B8893C)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Center</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <LiveClock />
          <LivePulse ago={ago} />
          <button onClick={refresh} className="glass-card flex items-center gap-2 rounded-xl px-4 py-2 text-[11px] font-black uppercase tracking-widest text-gold hover:text-gold-light transition-colors">
            <span className="material-symbols-outlined text-[16px]">refresh</span> Sync
          </button>
        </div>
      </div>

      {/* ── KPI row ── */}
      <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4 stagger-children">
        <KpiTile icon="payments" label="Total Revenue" value={stats.totalRevenue} money accent={CHART.gold} spark={revSpark} />
        <KpiTile icon="orders" label="Orders" value={stats.totalOrders} accent={CHART.violet}
          spark={chartData.map(m => ({ v: m.Revenue ? 1 + m.Revenue / 5000 : 0 }))} />
        <KpiTile icon="diversity_3" label="Devotees" value={stats.totalUsers} accent={CHART.tulsi} />
        <KpiTile icon="sensors" label="Centres Live" value={liveCentres.length} suffix={` / ${stats.totalCentres || 0}`} accent={CHART.lotus} />
      </div>

      {/* ── Charts row ── */}
      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3 stagger-children">
        {/* Monthly sacred revenue — 2 series area */}
        <ChartCard className="xl:col-span-2" title="Sacred Revenue · Monthly"
          subtitle="Live aggregation across donations & centre offerings — click a month to open its day-by-day story"
          right={<div className="flex items-center gap-3"><DrillHint /><DeltaBadge series={chartData.map(m => m.Revenue)} /><LivePulse ago={ago} /></div>}>
          <div className="h-72 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}
                onClick={chartDrill(p => { const k = monthKeyFromLabel(p.month); return k && { type: 'month', key: k }; })}>
                <defs>
                  <linearGradient id="gDon" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.gold} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={CHART.gold} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gOff" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.tulsi} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART.tulsi} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: CHART.axis, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: CHART.axis, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={38} />
                <Tooltip content={<CosmicTooltip />} cursor={{ stroke: 'rgba(246,220,150,.35)', strokeWidth: 1 }} />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} iconType="plainline" />
                <Area type="monotone" name="Donations" dataKey="Donations" stroke={CHART.gold} strokeWidth={2} fill="url(#gDon)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" name="Offerings" dataKey="Offerings" stroke={CHART.tulsi} strokeWidth={2} fill="url(#gOff)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Revenue mix — horizontal bars, direct-labeled */}
        <ChartCard title="Revenue Mix" subtitle={`Total ${fmtINR(stats.totalRevenue)}`} right={<DrillHint />}>
          <div className="h-72 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mixData} layout="vertical" margin={{ top: 8, right: 66, bottom: 0, left: 0 }} barSize={22}
                onClick={chartDrill(p => p.streamKey && { type: 'stream', key: p.streamKey })}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fill: '#E5DCC4', fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} width={92} />
                <Tooltip content={<CosmicTooltip />} cursor={{ fill: 'rgba(246,220,150,.06)' }} />
                <Bar dataKey="value" name="Revenue" radius={[0, 6, 6, 0]}>
                  {mixData.map((_, i) => <Cell key={i} fill={CHART.categorical[i]} />)}
                  <LabelList dataKey="value" position="right" formatter={(v) => fmtINR(v)} style={{ fill: '#F4EBD8', fontSize: 11, fontWeight: 800 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      {/* ── Bottom row: live centres + order feed ── */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3 stagger-children">
        {/* Live centres */}
        <ChartCard title="Sacred Destinations" subtitle={`${liveCentres.length} streaming live now`}
          right={<button onClick={() => navigate('/centres')} className="text-[10px] font-black uppercase tracking-widest text-gold hover:text-gold-light">Manage ❯</button>}>
          <div className="space-y-3">
            {centres.slice(0, 5).map(t => (
              <div key={t._id} role="button" tabIndex={0}
                onClick={() => setDrill({ type: 'centre', key: t._id })}
                onKeyDown={(e) => e.key === 'Enter' && setDrill({ type: 'centre', key: t._id })}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-[rgba(229,192,107,.12)] bg-white/[0.03] p-2.5 transition-colors hover:bg-white/[0.06]">
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-white/5">
                  {t.coverImage && <img src={t.coverImage} alt="" loading="lazy" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-bold text-ivory">{t.name}</p>
                  <p className="truncate text-[10px] font-semibold text-muted">{t.location}</p>
                </div>
                {t.isLive ? (
                  <span className="flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/15 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-red-400">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400"></span> Live
                  </span>
                ) : (
                  <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-stone-400">Idle</span>
                )}
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Recent orders — live feed */}
        <ChartCard className="xl:col-span-2" title="Order Stream" subtitle="Most recent sacred commerce · auto-refreshing"
          right={<button onClick={() => navigate('/orders')} className="text-[10px] font-black uppercase tracking-widest text-gold hover:text-gold-light">All Orders ❯</button>}>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[rgba(229,192,107,.14)] text-[9px] font-black uppercase tracking-[0.18em]">
                  <th className="pb-2.5 pr-4">Devotee</th>
                  <th className="pb-2.5 pr-4">Offering</th>
                  <th className="pb-2.5 pr-4">Amount</th>
                  <th className="pb-2.5 pr-4">Status</th>
                  <th className="pb-2.5">When</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o._id || o.id} onClick={() => o.status && setDrill({ type: 'status', key: o.status })}
                    title="Open every order at this funnel stage"
                    className="cursor-pointer border-b border-white/[0.04] text-[12px] transition-colors hover:bg-white/[0.03]">
                    <td className="py-2.5 pr-4 font-bold text-ivory">{o.userId?.name || o.userName || '—'}</td>
                    <td className="max-w-[220px] truncate py-2.5 pr-4 font-semibold text-stone-300">{o.productName || o.productId?.name || '—'}</td>
                    <td className="py-2.5 pr-4 font-bold tabular-nums text-gold-light">{fmtINR(o.totalAmount)}</td>
                    <td className="py-2.5 pr-4"><StatusChip status={o.status} /></td>
                    <td className="py-2.5 text-[10px] font-semibold text-muted">
                      {o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-[11px] font-bold uppercase tracking-widest text-muted">No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-right text-[10px] font-semibold text-muted">
            {fmtNum(stats.totalUnits)} units sold · {fmtNum(stats.pendingOrders)} pending fulfilment
          </p>
        </ChartCard>
      </div>

      {/* ── drill-down modal (opens on any chart click) ── */}
      <DrillDown drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
};

export default Dashboard;
